import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiMapPin, FiPhone, FiFilter } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { api } from '../../utils/api';
import { AUSTRALIAN_AREA_CODES, formatAreaCodeDisplay, type AustralianAreaCode } from '../../constants/australianAreaCodes';

interface SearchResult {
  number: string;
  formatted: string;
  region: {
    name: string;
    state: string;
    country: string;
  };
  monthlyFee: number;
  capabilities: string[];
  available: boolean;
  provider: 'telnyx' | 'twilio'; // Added provider field
}

interface SearchMeta {
  source: 'telnyx' | 'twilio' | 'multi-provider' | 'mock';
  count: number;
  providers?: string[];
  breakdown?: {
    telnyx: number;
    twilio: number;
  };
}

interface NumberSearchPanelProps {
  organizationId: string;
  onSelectNumber: (number: SearchResult) => void;
}

export const NumberSearchPanel: React.FC<NumberSearchPanelProps> = ({
  organizationId,
  onSelectNumber
}) => {
  const [country, setCountry] = useState('US');
  const [areaCode, setAreaCode] = useState('');
  const [selectedAuLocation, setSelectedAuLocation] = useState<AustralianAreaCode | null>(null);
  const [numberType, setNumberType] = useState<'LOCAL' | 'TOLL_FREE' | 'MOBILE'>('LOCAL');
  const [features, setFeatures] = useState<string[]>(['voice', 'sms']);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchMeta, setSearchMeta] = useState<SearchMeta | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    setIsSearching(true);
    setHasSearched(true);

    try {
      // Build search payload
      const searchPayload: any = {
        country,
        type: numberType,
        features,
        limit: 20
      };

      // For Australian numbers, use selected location data if available
      if (country === 'AU' && selectedAuLocation) {
        searchPayload.areaCode = selectedAuLocation.code;
        searchPayload.locality = selectedAuLocation.locality;
        searchPayload.administrativeArea = selectedAuLocation.administrativeArea;
      } else if (areaCode) {
        // For other countries, use manual area code input
        searchPayload.areaCode = areaCode;
      }

      const response = await api.post(
        `/organizations/${organizationId}/phone-numbers/search`,
        searchPayload
      );

      const numbers = response.data.numbers || [];
      const meta = response.data.meta || { source: 'mock', count: numbers.length };
      
      setSearchResults(numbers);
      setSearchMeta(meta);
      
      if (numbers.length === 0) {
        toast('No numbers found. Try different search criteria.', { icon: 'ℹ️' });
      } else {
        let sourceMessage = '';
        if (meta.source === 'multi-provider') {
          const telnyxCount = meta.breakdown?.telnyx || 0;
          const twilioCount = meta.breakdown?.twilio || 0;
          sourceMessage = `(${telnyxCount} from Telnyx, ${twilioCount} from Twilio)`;
        } else if (meta.source === 'telnyx') {
          sourceMessage = 'from Telnyx';
        } else if (meta.source === 'twilio') {
          sourceMessage = 'from Twilio';
        } else {
          sourceMessage = '(mock data)';
        }
        toast.success(`Found ${numbers.length} available numbers ${sourceMessage}`);
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = error instanceof Error && 'response' in error
        ? ((error as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to search numbers')
        : 'Failed to search numbers';
      toast.error(errorMessage);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const toggleFeature = (feature: string) => {
    setFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  return (
    <div className="space-y-6">
      {/* Search Filters */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FiFilter className="text-blue-400" />
          Search Criteria
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Country */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Country</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>

          {/* Area Code / Location */}
          <div>
            {country === 'AU' ? (
              <>
                <label className="block text-sm text-gray-400 mb-2">
                  Australian City
                </label>
                <select
                  value={selectedAuLocation ? `${selectedAuLocation.code}-${selectedAuLocation.city}` : ''}
                  onChange={(e) => {
                    if (!e.target.value) {
                      setSelectedAuLocation(null);
                      return;
                    }
                    const [code, city] = e.target.value.split('-');
                    const location = AUSTRALIAN_AREA_CODES.find(
                      ac => ac.code === code && ac.city === city
                    );
                    setSelectedAuLocation(location || null);
                  }}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Select city...</option>
                  {AUSTRALIAN_AREA_CODES.map((location, idx) => (
                    <option key={idx} value={`${location.code}-${location.city}`}>
                      {formatAreaCodeDisplay(location)}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="block text-sm text-gray-400 mb-2">
                  Area Code <span className="text-gray-500 text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="e.g. 415"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  maxLength={3}
                />
              </>
            )}
          </div>

          {/* Number Type */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Type</label>
            <select
              value={numberType}
              onChange={(e) => setNumberType(e.target.value as 'LOCAL' | 'TOLL_FREE' | 'MOBILE')}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="LOCAL">Local</option>
              <option value="TOLL_FREE">Toll-Free</option>
              <option value="MOBILE">Mobile</option>
            </select>
          </div>

          {/* Search Button */}
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {isSearching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <FiSearch />
                  Search
                </>
              )}
            </button>
          </div>
        </div>

        {/* Features */}
        <div className="mt-4">
          <label className="block text-sm text-gray-400 mb-2">Required Features</label>
          <div className="flex flex-wrap gap-2">
            {['voice', 'sms', 'mms'].map((feature) => (
              <button
                key={feature}
                onClick={() => toggleFeature(feature)}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  features.includes(feature)
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                {feature.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search Results */}
      {hasSearched && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FiPhone className="text-green-400" />
              Available Numbers {searchResults.length > 0 && `(${searchResults.length})`}
            </h3>
            {searchMeta && (
              <div className="flex items-center gap-2">
                {searchMeta.source === 'multi-provider' ? (
                  <>
                    <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      Telnyx: {searchMeta.breakdown?.telnyx || 0}
                    </span>
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full flex items-center gap-1">
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></span>
                      Twilio: {searchMeta.breakdown?.twilio || 0}
                    </span>
                  </>
                ) : searchMeta.source === 'telnyx' ? (
                  <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    Live Telnyx Data
                  </span>
                ) : searchMeta.source === 'twilio' ? (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full flex items-center gap-1">
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></span>
                    Live Twilio Data
                  </span>
                ) : (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full flex items-center gap-1">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                    Mock Data
                  </span>
                )}
              </div>
            )}
          </div>

          {searchResults.length === 0 ? (
            <div className="text-center py-12">
              <FiMapPin className="mx-auto text-gray-600 text-4xl mb-4" />
              <p className="text-gray-400">No numbers available with these criteria</p>
              <p className="text-gray-500 text-sm mt-2">Try a different area code or number type</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {searchResults.map((result, index) => (
                <motion.div
                  key={result.number}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-blue-500/50 transition-all group"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <FiPhone className="text-blue-400" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-lg">{result.formatted}</p>
                          {result.provider && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              result.provider === 'telnyx'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                            }`}>
                              {result.provider.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <FiMapPin className="text-gray-500 text-xs" />
                          <p className="text-gray-400 text-sm">
                            {result.region.name}, {result.region.state}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {Array.isArray(result.capabilities) && result.capabilities.map((cap) => (
                        <span
                          key={typeof cap === 'string' ? cap : JSON.stringify(cap)}
                          className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full"
                        >
                          {typeof cap === 'string' ? cap.toUpperCase() : String(cap).toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-white font-semibold text-lg">
                      ${result.monthlyFee.toFixed(2)}
                      <span className="text-gray-400 text-sm font-normal">/mo</span>
                    </p>
                    <button
                      onClick={() => onSelectNumber(result)}
                      className="mt-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      Select
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

